import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Modal, Button, Slider, Space, Typography, Row, Col } from 'antd';
import { RotateLeftOutlined, RotateRightOutlined } from '@ant-design/icons';

const { Text } = Typography;

interface ImageCropModalProps {
  visible: boolean;
  imageUrl: string;
  onCancel: () => void;
  onCrop: (croppedImageUrl: string) => void;
  aspectRatio?: number;
}

const ImageCropModal: React.FC<ImageCropModalProps> = ({
  visible,
  imageUrl,
  onCancel,
  onCrop,
  aspectRatio = 1
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [cropArea, setCropArea] = useState({
    x: 0,
    y: 0,
    width: 200,
    height: 200
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [dragType, setDragType] = useState<'move' | 'resize' | null>(null);
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);
  const [hoverHandle, setHoverHandle] = useState<string | null>(null);

  // 处理图片加载
  const handleImageLoad = useCallback(() => {
    if (imageRef.current) {
      const img = imageRef.current;
      const canvas = canvasRef.current;
      if (canvas) {
        // 设置画布大小
        const maxSize = 600;
        const scale = Math.min(maxSize / img.naturalWidth, maxSize / img.naturalHeight, 1);
        canvas.width = img.naturalWidth * scale;
        canvas.height = img.naturalHeight * scale;
        
        // 计算初始裁剪区域（正方形）
        const size = Math.min(canvas.width, canvas.height) * 0.8;
        setCropArea({
          x: (canvas.width - size) / 2,
          y: (canvas.height - size) / 2,
          width: size,
          height: size
        });
        
        setImageLoaded(true);
        drawImage();
      }
    }
  }, []);

  // 绘制图片和裁剪框
  const drawImage = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img || !imageLoaded) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 清空画布
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // 保存状态
    ctx.save();

    // 应用旋转
    if (rotation !== 0) {
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.translate(-canvas.width / 2, -canvas.height / 2);
    }

    // 绘制图片
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    // 恢复状态
    ctx.restore();

    // 绘制半透明遮罩
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // 清除裁剪区域，显示原始图片
    ctx.clearRect(cropArea.x, cropArea.y, cropArea.width, cropArea.height);
    
    // 在裁剪区域重新绘制图片
    ctx.save();
    if (rotation !== 0) {
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.translate(-canvas.width / 2, -canvas.height / 2);
    }
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    ctx.restore();

    // 绘制裁剪框边框
    ctx.strokeStyle = '#1890ff';
    ctx.lineWidth = 2;
    ctx.setLineDash([]);
    ctx.strokeRect(cropArea.x, cropArea.y, cropArea.width, cropArea.height);

    // 绘制角落控制点
    const cornerSize = 8;
    ctx.fillStyle = '#1890ff';
    
    // 四个角落的控制点
    const corners = [
      { x: cropArea.x, y: cropArea.y },
      { x: cropArea.x + cropArea.width, y: cropArea.y },
      { x: cropArea.x, y: cropArea.y + cropArea.height },
      { x: cropArea.x + cropArea.width, y: cropArea.y + cropArea.height }
    ];

    corners.forEach(corner => {
      ctx.fillRect(corner.x - cornerSize / 2, corner.y - cornerSize / 2, cornerSize, cornerSize);
    });
  }, [cropArea, imageLoaded, rotation]);

  // 当裁剪区域或图片加载状态改变时重新绘制
  useEffect(() => {
    if (imageLoaded) {
      drawImage();
    }
  }, [cropArea, imageLoaded, drawImage]);

  // 检查是否点击在角落控制点上
  const getResizeHandle = (x: number, y: number) => {
    const cornerSize = 12; // 增大控制点检测区域
    const corners = [
      { x: cropArea.x, y: cropArea.y, handle: 'nw' },
      { x: cropArea.x + cropArea.width, y: cropArea.y, handle: 'ne' },
      { x: cropArea.x, y: cropArea.y + cropArea.height, handle: 'sw' },
      { x: cropArea.x + cropArea.width, y: cropArea.y + cropArea.height, handle: 'se' }
    ];

    for (const corner of corners) {
      if (x >= corner.x - cornerSize / 2 && x <= corner.x + cornerSize / 2 &&
          y >= corner.y - cornerSize / 2 && y <= corner.y + cornerSize / 2) {
        console.log('检测到角落控制点:', corner.handle, '位置:', corner.x, corner.y);
        return corner.handle;
      }
    }
    return null;
  };

  // 处理鼠标按下
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    console.log('鼠标按下位置:', x, y);
    console.log('当前裁剪区域:', cropArea);

    // 检查是否点击在角落控制点上
    const handle = getResizeHandle(x, y);
    if (handle) {
      console.log('开始调整大小，控制点:', handle);
      setIsDragging(true);
      setDragType('resize');
      setResizeHandle(handle);
      setDragStart({ x, y });
      return;
    }

    // 检查是否点击在裁剪框内
    if (x >= cropArea.x && x <= cropArea.x + cropArea.width &&
        y >= cropArea.y && y <= cropArea.y + cropArea.height) {
      console.log('开始移动裁剪框');
      setIsDragging(true);
      setDragType('move');
      setDragStart({ x: x - cropArea.x, y: y - cropArea.y });
    }
  };

  // 处理鼠标移动
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // 检测鼠标悬停的控制点
    if (!isDragging) {
      const handle = getResizeHandle(x, y);
      setHoverHandle(handle);
    }

    if (!isDragging) return;

    if (dragType === 'move') {
      // 移动裁剪框
      setCropArea(prev => ({
        ...prev,
        x: Math.max(0, Math.min(canvas.width - prev.width, x - dragStart.x)),
        y: Math.max(0, Math.min(canvas.height - prev.height, y - dragStart.y))
      }));
    } else if (dragType === 'resize' && resizeHandle) {
      // 调整裁剪框大小
      const deltaX = x - dragStart.x;
      const deltaY = y - dragStart.y;
      
      console.log('调整大小 - 控制点:', resizeHandle, '增量:', deltaX, deltaY);
      
      setCropArea(prev => {
        let newArea = { ...prev };
        
        switch (resizeHandle) {
          case 'se': // 右下角
            newArea.width = Math.max(50, prev.width + deltaX);
            newArea.height = Math.max(50, prev.height + deltaY);
            break;
          case 'sw': // 左下角
            newArea.x = Math.max(0, prev.x + deltaX);
            newArea.width = Math.max(50, prev.width - deltaX);
            newArea.height = Math.max(50, prev.height + deltaY);
            break;
          case 'ne': // 右上角
            newArea.width = Math.max(50, prev.width + deltaX);
            newArea.y = Math.max(0, prev.y + deltaY);
            newArea.height = Math.max(50, prev.height - deltaY);
            break;
          case 'nw': // 左上角
            newArea.x = Math.max(0, prev.x + deltaX);
            newArea.width = Math.max(50, prev.width - deltaX);
            newArea.y = Math.max(0, prev.y + deltaY);
            newArea.height = Math.max(50, prev.height - deltaY);
            break;
        }
        
        // 确保不超出画布边界
        newArea.x = Math.max(0, Math.min(canvas.width - newArea.width, newArea.x));
        newArea.y = Math.max(0, Math.min(canvas.height - newArea.height, newArea.y));
        newArea.width = Math.min(canvas.width - newArea.x, newArea.width);
        newArea.height = Math.min(canvas.height - newArea.y, newArea.height);
        
        console.log('新的裁剪区域:', newArea);
        return newArea;
      });
      
      setDragStart({ x, y });
    }
  };

  // 处理鼠标释放
  const handleMouseUp = () => {
    setIsDragging(false);
    setDragType(null);
    setResizeHandle(null);
  };

  // 处理旋转
  const handleRotate = (direction: 'left' | 'right') => {
    setRotation(prev => prev + (direction === 'left' ? -90 : 90));
  };

  // 应用裁剪
  const handleApplyCrop = () => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return;

    // 创建新的画布用于裁剪
    const cropCanvas = document.createElement('canvas');
    const cropCtx = cropCanvas.getContext('2d');
    if (!cropCtx) return;

    // 设置裁剪画布大小
    cropCanvas.width = cropArea.width;
    cropCanvas.height = cropArea.height;

    // 计算原始图片的裁剪区域
    const scaleX = img.naturalWidth / canvas.width;
    const scaleY = img.naturalHeight / canvas.height;
    
    const sourceX = cropArea.x * scaleX;
    const sourceY = cropArea.y * scaleY;
    const sourceWidth = cropArea.width * scaleX;
    const sourceHeight = cropArea.height * scaleY;

    // 绘制裁剪区域
    cropCtx.drawImage(
      img,
      sourceX, sourceY, sourceWidth, sourceHeight,
      0, 0, cropCanvas.width, cropCanvas.height
    );

    // 转换为blob并创建URL
    cropCanvas.toBlob((blob) => {
      if (blob) {
        const croppedUrl = URL.createObjectURL(blob);
        onCrop(croppedUrl);
      }
    }, 'image/png', 0.9);
  };

  return (
    <Modal
      title="LOGO剪裁"
      open={visible}
      onCancel={onCancel}
      width={800}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          取消
        </Button>,
        <Button key="crop" type="primary" onClick={handleApplyCrop}>
          应用剪裁
        </Button>
      ]}
    >
      <div style={{ textAlign: 'center' }}>
        <div style={{ marginBottom: '16px' }}>
          <img
            ref={imageRef}
            src={imageUrl}
            alt="裁剪预览"
            style={{ display: 'none' }}
            onLoad={handleImageLoad}
          />
          <canvas
            ref={canvasRef}
            style={{ 
              border: '1px solid #d9d9d9', 
              maxWidth: '100%', 
              maxHeight: '400px',
              cursor: isDragging 
                ? (dragType === 'move' ? 'grabbing' : 'nw-resize')
                : hoverHandle 
                  ? (hoverHandle === 'nw' || hoverHandle === 'se' ? 'nw-resize' : 'ne-resize')
                  : 'default'
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={() => {
              handleMouseUp();
              setHoverHandle(null);
            }}
          />
        </div>

        <Row gutter={[16, 16]}>
          <Col span={24}>
            <div>
              <Text strong>旋转: {rotation}°</Text>
              <div style={{ marginTop: '8px' }}>
                <Space>
                  <Button 
                    icon={<RotateLeftOutlined />} 
                    onClick={() => handleRotate('left')}
                    size="small"
                  >
                    左转90°
                  </Button>
                  <Button 
                    icon={<RotateRightOutlined />} 
                    onClick={() => handleRotate('right')}
                    size="small"
                  >
                    右转90°
                  </Button>
                </Space>
              </div>
            </div>
          </Col>
        </Row>

        <div style={{ marginTop: '16px' }}>
          <Text type="secondary">
            拖拽裁剪框内部来移动位置，拖拽角落控制点来调整大小，使用旋转按钮来调整图片方向
          </Text>
        </div>
      </div>
    </Modal>
  );
};

export default ImageCropModal;
